import React, { useState, useCallback } from 'react';
import { FM110 } from '../../../services/m201Api';
import LookupField from '../../../components/LookupField';
import { AcceptanceFieldHint, pwm201f1AcceptanceManifest } from '../../../features/acceptance';

interface Tab1Props {
  formData: Partial<FM110>;
  isEditing: boolean;
  getField: (key: keyof FM110) => FM110[keyof FM110];
  setField: (key: keyof FM110, value: FM110[keyof FM110]) => void;
  documentKey?: string;
}

/** Convert ISO date string to date input value */
const dateToInput = (v: FM110[keyof FM110]): string => {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.substring(0, 10) : s;
};

const Tab1BasicInfo: React.FC<Tab1Props> = ({ formData, isEditing, getField, setField, documentKey }) => {
  const isDisabled = !isEditing;

  // Dynamic description labels (Label2/5/6/8 in FoxPro)
  const [deptName, setDeptName] = useState('');
  const [warehouseName, setWarehouseName] = useState('');
  const [categoryName, setCategoryName] = useState('');

  const handleDeptChange = useCallback((code: string, name?: string) => {
    setField('fm110_16', code);
    setDeptName(name || '');
  }, [setField]);

  const handleWarehouseChange = useCallback((code: string, name?: string) => {
    setField('fm110_13', code);
    setWarehouseName(name || '');
  }, [setField]);

  const handleCategoryChange = useCallback((code: string, name?: string) => {
    setField('fm110_17', code);
    setCategoryName(name || '');
  }, [setField]);

  return (
    <div className="m201-master-fields">
      {/* ── Row 1: 異動單號 | (已核准) | 異動日期 | (異常) ── */}
      <div className="m201-row">
        <label className="m201-lbl">異動單號</label>
        <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm110_01" documentKey={documentKey} />
        <input
          className="m201-inp"
          type="text"
          readOnly
          value={(getField('fm110_01') as string) || ''}
          style={{ width: 79 }} data-contract-field="fm110_01"
        />
        <span
          className="m201-desc field-label"
          data-contract-field="fm110_80"
          style={{ minWidth: 60 }}
        >
          <span>核准狀態：</span>
          <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm110_80" documentKey={documentKey} />
          {formData.fm110_80 === 'P' && (
            <span className="m201-flag m201-flag-approved">已核准</span>
          )}
        </span>

        <label className="m201-lbl m201-lbl-r">異動日期</label>
        <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm110_11" documentKey={documentKey} />
        <input
          className="m201-inp"
          type="date"
          disabled={isDisabled}
          value={dateToInput(getField('fm110_11'))}
          onChange={e => setField('fm110_11', e.target.value || null)}
          style={{ width: 103 }} data-contract-field="fm110_11"
        />
        <span className="m201-desc">
          {!!formData.fm110_21 && (
            <span className="m201-flag m201-flag-abnormal">異常</span>
          )}
        </span>
      </div>

      {/* ── Row 2: 部門代號 | Label6 | 輸單人員 | Label5 ── */}
      <div className="m201-row">
        <label className="m201-lbl required">部門代號</label>
        <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm110_16" documentKey={documentKey} />
        <LookupField
          lookupType="department"
          value={(getField('fm110_16') as string) || ''}
          onChange={handleDeptChange}
          disabled={isDisabled}
          maxLength={4}
          style={{ width: 79 }}
          title="部門查詢" contractFieldId="fm110_16"
        />
        <span className="m201-desc">{deptName}</span>

        <label className="m201-lbl m201-lbl-r">輸單人員</label>
        <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm110_15" documentKey={documentKey} />
        <input
          className="m201-inp"
          type="text"
          readOnly
          value={(getField('fm110_15') as string) || ''}
          style={{ width: 103 }} data-contract-field="fm110_15"
        />
        <span className="m201-desc"></span>
      </div>

      {/* ── Row 3: 庫位 | Label2 | 異動類別 | Label8 | +/- ── */}
      <div className="m201-row">
        <label className="m201-lbl required">庫&emsp;&emsp;位</label>
        <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm110_13" documentKey={documentKey} />
        <LookupField
          lookupType="warehouse"
          value={(getField('fm110_13') as string) || ''}
          onChange={handleWarehouseChange}
          disabled={isDisabled}
          maxLength={5}
          style={{ width: 79 }}
          title="庫位查詢" contractFieldId="fm110_13"
        />
        <span className="m201-desc">{warehouseName}</span>

        <label className="m201-lbl m201-lbl-r required">異動類別</label>
        <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm110_17" documentKey={documentKey} />
        <LookupField
          lookupType="transferReason"
          value={(getField('fm110_17') as string) || ''}
          onChange={handleCategoryChange}
          disabled={isDisabled}
          maxLength={1}
          style={{ width: 39 }}
          title="異動類別查詢" contractFieldId="fm110_17"
        />
        <span className="m201-desc" style={{ minWidth: 40 }}>{categoryName}</span>

        {/* fm110_18: FoxPro auto-fills via BR-C04, React needs manual input */}
        <label className="m201-lbl-sm required">+/-</label>
        <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm110_18" documentKey={documentKey} />
        <select
          className="m201-inp"
          disabled={isDisabled}
          value={(getField('fm110_18') as string) || ''}
          onChange={e => setField('fm110_18', e.target.value)}
          style={{ width: 52 }} data-contract-field="fm110_18"
        >
          <option value="">--</option>
          <option value="+">+</option>
          <option value="-">-</option>
        </select>
      </div>

      {/* ── Row 4: 處理對策 (wide textarea, readOnly) ── */}
      <div className="m201-row m201-row-wide">
        <label className="m201-lbl">處理對策</label>
        <AcceptanceFieldHint manifest={pwm201f1AcceptanceManifest} fieldId="fm110_22" documentKey={documentKey} />
        <textarea
          className="m201-inp"
          readOnly
          rows={4}
          value={(getField('fm110_22') as string) || ''}
          onChange={e => setField('fm110_22', e.target.value)}
          style={{ flex: 1 }} data-contract-field="fm110_22"
        />
      </div>
    </div>
  );
};

export default Tab1BasicInfo;
